<template>
  <div v-if="loading" class="mpf-payment-view__loading">Loading…</div>

  <template v-else-if="payment">
    <p v-if="payment.state === 'already-paid'">This invoice has already been paid.</p>

    <p v-else-if="payment.state === 'cancelled'">
      This invoice has been cancelled and is no longer payable.
    </p>

    <p v-else-if="payment.state === 'unavailable'">
      Online payment is not currently available. Please contact
      {{ practiceName }} to arrange payment.
    </p>

    <template v-else>
      <MpfCard primary heading="Amount due" class="mpf-u-margin-bottom-6">
        <p class="mpf-payment-view__amount">{{ formattedAmountDue }}</p>
      </MpfCard>

      <section v-if="payment.isDemoSite" class="mpf-payment-view__demo">
        <MpfTag text="Demo Mode" colour="orange" />
        <p class="mpf-u-margin-top-2">
          This is a demo site, so no real payment is taken. Use the button below to simulate a
          successful payment.
        </p>
        <MpfButton
          label="Mark as Paid (Demo)"
          variant="secondary"
          :disabled="markingAsPaidDemo"
          prevent-double-click
          @click="handleMarkAsPaidDemo"
        />
      </section>

      <div v-else ref="stripeContainer" class="mpf-payment-view__stripe"></div>
    </template>
  </template>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import MpfCard from '../components/MpfCard/MpfCard.vue';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import MpfTag from '../components/MpfTag/MpfTag.vue';
  import { useTenantStore } from '@/stores/useTenantStore';
  import {
    fetchInvoicePaymentApi,
    markInvoicePaymentAsPaidDemoApi,
  } from '@/services/payment.service';
  import { loadStripeJs, getStripe } from '../stripe/loadStripeJs';
  import { InvoicePayment } from '@/types/payment';

  const route = useRoute();
  const router = useRouter();
  const tenantStore = useTenantStore();

  const invoiceId = route.params.invoiceId as string;

  const loading = ref(true);
  const markingAsPaidDemo = ref(false);
  const payment = ref<InvoicePayment | null>(null);
  const stripeContainer = ref<HTMLElement | null>(null);

  const practiceName = computed(() => tenantStore.tenant?.localOrganisation?.name);

  const formattedAmountDue = computed(() => {
    if (!payment.value) return '';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
      payment.value.amountDue / 100,
    );
  });

  async function mountStripeCheckout() {
    if (!payment.value?.clientSecret || !payment.value?.publishableKey || !stripeContainer.value) {
      return;
    }

    await loadStripeJs();

    const stripeOptions = payment.value.processorAccountId
      ? { stripeAccount: payment.value.processorAccountId }
      : {};

    const stripe = getStripe()(payment.value.publishableKey, stripeOptions);
    const checkout = await stripe.initEmbeddedCheckout({ clientSecret: payment.value.clientSecret });

    checkout.mount(stripeContainer.value);
  }

  async function handleMarkAsPaidDemo() {
    markingAsPaidDemo.value = true;
    try {
      await markInvoicePaymentAsPaidDemoApi(invoiceId);
      await router.push({
        name: 'payment-confirmation',
        params: { tenantId: route.params.tenantId, invoiceId },
      });
    } finally {
      markingAsPaidDemo.value = false;
    }
  }

  onMounted(async () => {
    try {
      payment.value = await fetchInvoicePaymentApi(invoiceId);
    } finally {
      loading.value = false;
    }

    if (payment.value?.state === 'payable' && !payment.value.isDemoSite) {
      await nextTick(); // let the v-else branch render the mount target div first
      await mountStripeCheckout();
    }
  });
</script>

<style scoped lang="scss">
  .mpf-payment-view__amount {
    font-size: 2.5rem;
    font-weight: 700;
  }

  .mpf-payment-view__stripe {
    min-height: 30rem;
  }
</style>
